/**
 * Dashboard page - Main landing page after login.
 * Displays key statistics and quick actions.
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import {
  BookOpen,
  Users,
  Building2,
  ArrowRight,
  Clock,
  MapPin,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  BarChart3,
  Library,
  RefreshCw,
  Bookmark,
  Wallet,
  CalendarCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { get } from "@/api/client";
import type { LucideIcon } from "lucide-react";

interface DashboardData {
  circulation: {
    activeLoans: number;
    overdueLoans: number;
    loansToday: number;
    returnsToday: number;
    reservationsPending: number;
  };
  collection: {
    totalRecords: number;
    totalItems: number;
    availableItems: number;
    recordsAddedThisMonth: number;
  };
  acquisition: {
    budgetAvailableCents: number;
    budgetUtilizationPercent: number;
    pendingOrders: number;
  };
  patrons: {
    totalPatrons: number;
    activePatrons: number;
    newPatronsThisMonth: number;
  };
  alerts: Array<{
    type: "warning" | "error" | "info";
    category: string;
    message: string;
  }>;
  generatedAt: string;
}

export function DashboardPage() {
  const { user, isAdmin } = useAuth();

  const {
    data: stats,
    isLoading,
    error,
  } = useQuery<DashboardData>({
    queryKey: ["dashboard-stats"],
    queryFn: () => get<DashboardData>("/statistics/dashboard"),
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000,
  });

  const roleLabels: Record<string, string> = {
    ROLE_SUPER_ADMIN: "Super Administrateur",
    ROLE_ADMIN: "Administrateur",
    ROLE_CATALOGER: "Catalogueur",
    ROLE_CIRCULATION: "Agent de prêt",
    ROLE_ACQUISITION: "Responsable acquisitions",
    ROLE_USER: "Utilisateur",
  };

  const primaryRole = user?.roles.find((r) => r !== "ROLE_USER") || "ROLE_USER";

  return (
    <div className="space-y-8">
      {/* Welcome section */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Bonjour, {user?.firstName} !
            </h1>
            <p className="mt-1 text-gray-500 dark:text-gray-400">{roleLabels[primaryRole]}</p>
          </div>
          <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600 dark:bg-gray-700 dark:text-gray-400">
            <MapPin className="h-4 w-4" />
            <span>{user?.location?.name || "Aucun établissement"}</span>
          </div>
        </div>

        {user?.lastLoginAt && (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Clock className="h-4 w-4" />
            <span>
              Dernière connexion :{" "}
              {new Date(user.lastLoginAt).toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        )}
      </div>

      {/* Alerts */}
      {stats?.alerts && stats.alerts.length > 0 && (
        <div className="space-y-2">
          {stats.alerts.map((alert, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 rounded-xl border p-4 ${
                alert.type === "error"
                  ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
                  : alert.type === "warning"
                    ? "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20"
                    : "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20"
              }`}
            >
              <AlertTriangle
                className={`h-5 w-5 flex-shrink-0 ${
                  alert.type === "error"
                    ? "text-red-500"
                    : alert.type === "warning"
                      ? "text-amber-500"
                      : "text-blue-500"
                }`}
              />
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {alert.message}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          Actions rapides
        </h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Catalog card - Available to all */}
          <QuickActionCard
            title="Catalogue"
            description="Rechercher et consulter les notices bibliographiques"
            icon={BookOpen}
            href="/catalog"
          />

          {/* Admin cards - Only for admins */}
          {isAdmin && (
            <>
              <QuickActionCard
                title="Utilisateurs"
                description="Gérer les comptes utilisateurs"
                icon={Users}
                href="/admin/users"
              />
              <QuickActionCard
                title="Établissements"
                description="Gérer les bibliothèques et leurs antennes"
                icon={Building2}
                href="/admin/locations"
              />
            </>
          )}
        </div>
      </div>

      {/* Stats section */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Statistiques</h2>
          <Link
            to="/statistics"
            className="flex items-center gap-1 text-sm font-medium text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <BarChart3 className="h-4 w-4" />
            Voir les statistiques détaillées
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800"
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
                  <div className="flex-1">
                    <div className="h-4 w-20 rounded bg-gray-200 dark:bg-gray-700" />
                    <div className="mt-2 h-7 w-16 rounded bg-gray-200 dark:bg-gray-700" />
                    <div className="mt-2 h-3 w-24 rounded bg-gray-200 dark:bg-gray-700" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center dark:border-red-800 dark:bg-red-900/20">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
            <p className="mt-4 font-medium text-red-800 dark:text-red-200">
              Erreur lors du chargement des statistiques
            </p>
          </div>
        ) : stats ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Notices"
                value={stats.collection.totalRecords.toLocaleString("fr-FR")}
                subtitle={`+${String(stats.collection.recordsAddedThisMonth)} ce mois`}
                trend={stats.collection.recordsAddedThisMonth > 0 ? "up" : "stable"}
                icon={BookOpen}
                color="blue"
              />
              <StatCard
                label="Exemplaires"
                value={stats.collection.totalItems.toLocaleString("fr-FR")}
                subtitle={`${stats.collection.availableItems.toLocaleString("fr-FR")} disponibles`}
                icon={Library}
                color="purple"
              />
              <StatCard
                label="Prêts en cours"
                value={stats.circulation.activeLoans.toLocaleString("fr-FR")}
                subtitle={
                  stats.circulation.overdueLoans > 0
                    ? `${String(stats.circulation.overdueLoans)} en retard`
                    : "Aucun retard"
                }
                trend={stats.circulation.overdueLoans > 10 ? "down" : "stable"}
                alert={stats.circulation.overdueLoans > 10}
                icon={RefreshCw}
                color={stats.circulation.overdueLoans > 10 ? "amber" : "blue"}
              />
              <StatCard
                label="Réservations"
                value={stats.circulation.reservationsPending.toLocaleString("fr-FR")}
                subtitle="en attente"
                icon={Bookmark}
                color="green"
              />
            </div>

            {/* Secondary stats */}
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <StatCard
                label="Lecteurs inscrits"
                value={stats.patrons.totalPatrons.toLocaleString("fr-FR")}
                subtitle={`${stats.patrons.activePatrons.toLocaleString("fr-FR")} actifs`}
                icon={Users}
                color="purple"
              />
              <StatCard
                label="Prêts aujourd'hui"
                value={stats.circulation.loansToday.toLocaleString("fr-FR")}
                subtitle={`${String(stats.circulation.returnsToday)} retours`}
                icon={CalendarCheck}
                color="green"
              />
              <StatCard
                label="Budget disponible"
                value={formatCurrency(stats.acquisition.budgetAvailableCents)}
                subtitle={`${stats.acquisition.budgetUtilizationPercent.toFixed(0)}% utilisé`}
                trend={stats.acquisition.budgetUtilizationPercent > 90 ? "down" : "stable"}
                alert={stats.acquisition.budgetUtilizationPercent > 90}
                icon={Wallet}
                color={stats.acquisition.budgetUtilizationPercent > 90 ? "red" : "green"}
              />
            </div>

            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Clock className="h-3.5 w-3.5" />
              <span>
                Mis à jour :{" "}
                {new Date(stats.generatedAt).toLocaleTimeString("fr-FR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);
}

interface QuickActionCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  href: string;
  disabled?: boolean;
}

function QuickActionCard({ title, description, icon: Icon, href, disabled }: QuickActionCardProps) {
  if (disabled) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 opacity-50 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 text-gray-400 dark:bg-gray-700">
          <Icon className="h-6 w-6" />
        </div>
        <h3 className="mt-4 font-semibold text-gray-900 dark:text-white">{title}</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">(Bientôt disponible)</p>
      </div>
    );
  }

  return (
    <Link
      to={href}
      className="group rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-blue-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-600"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white dark:bg-blue-900/30">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="mt-4 font-semibold text-gray-900 dark:text-white">{title}</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
      <div className="mt-4 flex items-center text-sm font-medium text-blue-600 dark:text-blue-400">
        Accéder
        <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  subtitle?: string;
  trend?: "up" | "down" | "stable";
  alert?: boolean;
  icon?: LucideIcon;
  color?: "blue" | "green" | "purple" | "amber" | "red" | "gray";
}

function StatCard({
  label,
  value,
  subtitle,
  trend,
  alert,
  icon: Icon,
  color = "blue",
}: StatCardProps) {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400",
    green: "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400",
    purple: "bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400",
    amber: "bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400",
    red: "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400",
    gray: "bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  };

  const alertBorderClass = alert
    ? "border-amber-300 dark:border-amber-600"
    : "border-gray-200 dark:border-gray-700";

  return (
    <div className={`rounded-xl border bg-white p-5 dark:bg-gray-800 ${alertBorderClass}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          {Icon && (
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-lg ${colorClasses[color]}`}
            >
              <Icon className="h-5 w-5" />
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</p>
            <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            {subtitle && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
            )}
          </div>
        </div>
        {trend && trend !== "stable" && (
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full ${
              trend === "up"
                ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                : "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
            }`}
          >
            {trend === "up" ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <TrendingDown className="h-4 w-4" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
